import React, { memo, useEffect, useState } from "react";
import Table from "../shared/table/Table";
import { FiEdit3, FiEye } from "react-icons/fi";
import { useNavigate, useLocation } from "react-router-dom";
import {
  fetchData,
  fetchFormData,
  formatDateOnly,
  taskPriorityOptions,
} from "../../utils/options";
import { MdThumbDown, MdThumbUp } from "react-icons/md";
import topTost from "../../utils/topTost";
import TaskHistoryModal from "../../utils/TaskHistoryModal";
import SelectDropdown from "../shared/SelectDropdown";
import ConfirmModal from "../shared/ConfirmModal";
import Swal from "sweetalert2";

const TaskTable = ({ filteredData = null, permission }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const M_id = queryParams.get("id");
  const userId = localStorage.getItem("id");
  const [taskData, setTaskData] = useState([]);
  const [statusData, setStatusData] = useState([]);
  const [historyData, setHistoryData] = useState([]);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (localStorage.getItem("access_token") === null) {
      navigate("/");
    }
  }, []);

  const fetchtaskData = async () => {
    setLoading(true);
    try {
      Swal.fire({
        title: "Loading...",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const formData = new FormData();
      formData.append("userId", userId);
      formData.append("moduleId", M_id);

      const result = await fetchFormData("task/list", formData, "POST");
      const status = await fetchFormData(
        "task-status/listDrop",
        formData,
        "POST"
      );

      if (result) {
        setTaskData(result.data || []);
      }
      if (status) {
        setStatusData(
          status.data.map((val) => {
            return {
              value: val.id,
              label: val.task_status_name || "",
            };
          })
        );
      }
    } catch (error) {
      console.error("Error fetching initial data:", error);
      Swal.fire("Error", "Failed to load tasks", "error");
    } finally {
      setLoading(false);
      Swal.close();
    }
  };

  const TableCell = memo(({ options, defaultSelect, rowId }) => {
    const [selectedOption, setSelectedOption] = useState(defaultSelect || null);
    const [pendingOption, setPendingOption] = useState(null);
    const [showConfirm, setShowConfirm] = useState(false);

    const handleSelectChange = (option) => {
      setPendingOption(option);
      setShowConfirm(true);
    };

    const handleConfirm = async () => {
      try {
        const formData = new FormData();
        formData.append("id", rowId);
        formData.append("status", pendingOption.value);
        formData.append("userId", userId);
        formData.append("moduleId", M_id);

        const res = await fetchFormData("task/change-status", formData, "POST");

        if (res?.message === "Task status successfully updated") {
          topTost({
            title: res.message,
            icon: "success",
          });
          setSelectedOption();
          fetchtaskData();
        } else {
          topTost({
            title: res?.message || "Status update failed",
            icon: "error",
          });
        }
      } catch (error) {
        console.error("Status update error:", error);
        topTost({
          title: "Something went wrong",
          icon: "error",
        });
      } finally {
        setShowConfirm(false);

      }
    };

    console.log(taskData);
    console.log("mm", statusData);
    return (
      <>
        <SelectDropdown
          options={options}
          defaultSelect=""
          selectedOption={selectedOption}
          onSelectOption={handleSelectChange}
          value={selectedOption?.value}
        />

        <ConfirmModal
          show={showConfirm}
          title="Change Status"
          message="Are you sure you want to change the status?"
          confirmText="Yes"
          cancelText="No"
          onClose={() => setShowConfirm(false)}
          onConfirm={handleConfirm}
        />
      </>
    );
  });

  const columns = [
    ...(permission?.pf_edit === 1 || permission?.pf_status === 1
      ? [
          {
            accessorKey: "actions",
            header: () => "Actions",
            cell: ({ row }) => {
              const id = row.original.id;

              return (
                <div className="hstack gap-2 justify-content-end">
                  {permission?.pf_edit === 1 && (
                    <span
                      className="cursor-pointer"
                      onClick={() => navigate(`/task/history/${id}`)}
                      title="Task History"
                    >
                      <FiEye size={16} />
                    </span>
                  )}

                  {permission?.pf_edit === 1 && (
                    <span
                      className="cursor-pointer"
                      onClick={() =>
                        navigate(`/task/edit-task/${id}?id=${M_id}`)
                      }
                    >
                      <FiEdit3 size={16} />
                    </span>
                  )}
                </div>
              );
            },
            meta: {
              headerClassName: "width-30",
              exportable: false,
            },
          },
        ]
      : []),
    {
      accessorKey: "status_name",
      header: () => "Status",
      cell: (info) => {
        const statusValue = info.getValue(); // e.g., 2
        const selectedOption = statusData.find(
          (opt) => opt.label === statusValue
        );

        return (
          <TableCell
            options={statusData}
            defaultSelect={selectedOption}
            rowId={info.row.original.id}
          />
        );
      },
      defaultIsVisible: true,
    },
    {
      accessorKey: "task_Id",
      header: "Task id",
      cell: (info) => info.getValue(),
      defaultIsVisible: true, // Important
    },
    {
      accessorKey: "task_title",
      header: "Task Title",
      cell: (info) => info.getValue(),
      defaultIsVisible: true, // Important
    },
    {
      accessorKey: "task_details",
      header: "Task Details",
      cell: (info) => info.getValue(),
    },

    {
      accessorKey: "priority",
      header: "Priority",
      defaultIsVisible: true,
      cell: ({ getValue }) => {
        const value = getValue();

        const match = taskPriorityOptions.find((opt) => opt.value === value);
        const baseColor = match?.color || "#d9d9d9";
        const label = match?.label || value;
        const gradientBg = `linear-gradient(135deg, ${baseColor}20, ${baseColor}50)`; // with alpha

        return (
          <span
            style={{
              background: gradientBg,
              color: baseColor,
              padding: "4px 10px",
              display: "inline-block",
              fontSize: "12px",
              fontWeight: "500",
              textTransform: "capitalize",
            }}
          >
            {label}
          </span>
        );
      },
    },
    {
      accessorKey: "task_start_date",
      header: "Start Date",
         cell: (info) => formatDateOnly(info.getValue()),
      defaultIsVisible: true, // Important
    },
    {
      accessorKey: "task_end_date",
      header: "End Date",
          cell: (info) => formatDateOnly(info.getValue()),
      defaultIsVisible: true, // Important
    },

    {
      accessorKey: "category_name",
      header: "Category Name",
      cell: (info) => info.getValue(),
    },
    {
      accessorKey: "assigned_name",
      header: "Assigned To",
      cell: (info) => info.getValue(),
      defaultIsVisible: true, // Important
    },
    {
      accessorKey: "created_name",
      header: "Created By",
      cell: (info) => info.getValue(),
      defaultIsVisible: true, // Important
    },
    // {
    //   accessorKey: "status_name",
    //   header: "status name",
    //   cell: (info) => info.getValue(),
    //   enableColumnVisibility: false,
    // },
  ];

  useEffect(() => {
    const shouldFetch =
      !Array.isArray(filteredData) || filteredData.length === 0;

    if (shouldFetch) {
      fetchtaskData();
    } else {
      setTaskData(filteredData); //  Use filtered data passed as prop
    }
  }, [filteredData]);

  return (
    <>
      <Table data={taskData} columns={columns} />
    </>
  );
};
export default TaskTable;
